import { FDRStatus, OrgUserSearchParams } from '@cibo/core'
import {
  AuthUserPermission,
  Loading,
  PaginatedUsersTable,
  USER_DATA_KEYS,
  useDataGridPersistence,
  useFDRAcresColumn,
  useFDRFieldCountColumn,
  useOrgUserColumns,
  useOrganizationList,
  usePermissions,
  useWindowTitle,
} from '@cibo/profile'
import { useOrgUserProgramColumns } from '@cibo/programs'
import PersonAddRounded from '@mui/icons-material/PersonAddRounded'
import { Button } from '@mui/material'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ADMIN_ROUTES } from '../../../navigation/types'
import { GROWERS_ROUTES } from '../../../navigation/types/growersRoutes'
import { Forbidden } from '../../Forbidden'

interface GridOptionsQueryString {
  page?: number
  search?: OrgUserSearchParams
  sortModel?: GridSortModel
}

export const GrowersIndex = () => {
  const { t } = useTranslation('impact/pages/GrowersIndex')
  useWindowTitle(t('pageTitle'))
  const { can } = usePermissions()
  const navigate = useNavigate()
  const organizations = useOrganizationList()
  const [searchParams, setSearchParams] = useSearchParams()

  const availableColumns = useOrgUserColumns({})
  const enrolledAcresColumn = useFDRAcresColumn(FDRStatus.enrolled, {
    sortable: false,
    flex: 0.5,
    minWidth: 80,
  })
  const enrolledFieldCountColumn = useFDRFieldCountColumn(FDRStatus.enrolled, {
    sortable: false,
    flex: 0.5,
    minWidth: 80,
  })
  const eligibleAcresColumn = useFDRAcresColumn(FDRStatus.eligibleForEnrollment, {
    sortable: false,
    flex: 0.5,
    minWidth: 80,
  })
  const eligibleFieldCountColumn = useFDRFieldCountColumn(FDRStatus.eligibleForEnrollment, {
    sortable: false,
    flex: 0.5,
    minWidth: 80,
  })
  const orgUserProgramColumns = useOrgUserProgramColumns({
    userProgramPath: GROWERS_ROUTES.PROGRAM,
  })

  const columns = useMemo(
    () =>
      !!organizations.data?.length
        ? [
            availableColumns.grower,
            eligibleAcresColumn,
            orgUserProgramColumns.eligiblePrograms,
            eligibleFieldCountColumn,
            enrolledAcresColumn,
            enrolledFieldCountColumn,
            orgUserProgramColumns.potentialRewards,
            availableColumns.network,
          ]
        : [
            availableColumns.grower,
            orgUserProgramColumns.eligiblePrograms,
            eligibleAcresColumn,
            eligibleFieldCountColumn,
            enrolledAcresColumn,
            enrolledFieldCountColumn,
            orgUserProgramColumns.potentialRewards,
            availableColumns.network,
          ],
    [availableColumns, organizations.isFetched]
  )

  const persistenceProps = useDataGridPersistence({
    columns,
    key: USER_DATA_KEYS.USERS_TABLE_GLOBAL,
    options: ['columnOrder', 'columnVisibility', 'pageSize', 'columnWidth'],
  })

  const table = useMemo(() => {
    try {
      if (typeof searchParams.get('table') === 'string') {
        //@todo more sanity checks on what is parsed
        return JSON.parse(searchParams.get('table') as string) as GridOptionsQueryString
      }
    } catch (e) {
      console.log({ e })
    }
    return {} as GridOptionsQueryString
  }, [searchParams.get('table')])

  const updateSearchParams = (values: Record<string, any>) => {
    const paramsValue = JSON.stringify(values)
    setSearchParams({ table: paramsValue }, { replace: true })
  }

  if (!can({ useAny: AuthUserPermission.USER_DASHBOARD })) {
    return <Forbidden returnTo={'/'} />
  }

  if (!organizations.isFetched) {
    return <Loading />
  }

  return (
    <PaginatedUsersTable
      {...persistenceProps}
      baseQuery={{ persona: 'grower' }}
      availableFilters={{
        persona: false,
        organization: true,
        includeInactive: true,
        state: true,
        county: true,
        acres: true,
      }}
      goToUser={() => null}
      onRowClick={({ id }) => navigate(`./${id}`)}
      hideCellFocus
      onChangeSearchValues={search =>
        updateSearchParams({
          ...table,
          search,
        })
      }
      searchValues={table.search}
      page={table.page}
      actions={
        can({ useAny: AuthUserPermission.USER_CREATE }) ? (
          <Button
            color="secondary"
            variant="outlined"
            startIcon={<PersonAddRounded />}
            component={Link}
            to={ADMIN_ROUTES.USER_CREATE}
          >
            {t('addUser')}
          </Button>
        ) : null
      }
      onPageChange={page =>
        updateSearchParams({
          ...table,
          page,
        })
      }
      sortModel={table.sortModel}
      onSortModelChange={sortModel => {
        updateSearchParams({
          ...table,
          sortModel,
        })
      }}
    />
  )
}
